import { useState } from "react";
import { Upload, Play, Table2, Download, X, Database, AlertCircle, CheckCircle, Copy, Check } from "lucide-react";

export default function Text2SQLWithData() {
  const [nl, setNl] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [sql, setSql] = useState("");
  const [result, setResult] = useState(null);
  const [confidence, setConfidence] = useState(null);
  const [reasoning, setReasoning] = useState("");
  const [activeTab, setActiveTab] = useState("query");
  const [copied, setCopied] = useState(false);

  // Dataset state
  const [uploadedDataset, setUploadedDataset] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [datasetPreview, setDatasetPreview] = useState(null);

  const API_BASE = process.env.REACT_APP_API_ROOT; // Update with your backend URL

  
  // Upload dataset to backend
  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    setError("");

    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("table_name", "uploaded_data");

      const response = await fetch(`${API_BASE}/api/upload-dataset`, {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        throw new Error("Failed to upload file");
      }

      const data = await response.json();
      setUploadedDataset(data);
      setDatasetPreview(data.sample_data);
      setActiveTab("data");

      // Show success message
      setError("");
    } catch (err) {
      setError(err.message || "Failed to upload file");
    } finally {
      setUploading(false);
    }
  };

  // Convert natural language to SQL
  const onConvert = async () => {
    setLoading(true);
    setError("");
    setSql("");
    setResult(null);
    setReasoning("");

    try {
      const response = await fetch(`${API_BASE}/api/text2sql`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          query: nl,
          run: true,
          limit: 20,
          dataset_id: uploadedDataset?.dataset_id || null,
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to generate SQL");
      }

      const data = await response.json();
      setSql(data.sql);
      setConfidence(data.confidence);
      setReasoning(data.reasoning || "");
      setResult(data.result);
      setActiveTab("query");
    } catch (e) {
      setError(e.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  // Execute SQL manually
  const onRunSQL = async () => {
    if (!sql || !uploadedDataset) return;

    setError("");
    setResult(null);
    setLoading(true);

    try {
      const response = await fetch(`${API_BASE}/api/execute-sql`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          sql: sql,
          run: true,
          limit: 20,
          dataset_id: uploadedDataset.dataset_id,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Failed to execute SQL");
      }

      const data = await response.json();
      setResult(data.result);
      setActiveTab("query");
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  // Delete dataset
  const onDeleteDataset = async () => {
    if (!uploadedDataset) return;

    try {
      await fetch(`${API_BASE}/api/datasets/${uploadedDataset.dataset_id}`, {
        method: "DELETE",
      });

      setUploadedDataset(null);
      setDatasetPreview(null);
      setResult(null);
      setSql("");
    } catch (err) {
      console.error("Failed to delete dataset:", err);
    }
  };

  // Export results as CSV
  const exportToCSV = () => {
    if (!result || result.length === 0) return;

    const headers = Object.keys(result[0]);
    const csv = [
      headers.join(","),
      ...result.map(row => headers.map(h => JSON.stringify(row[h] || "")).join(","))
    ].join("\n");

    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "query_results.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleCopy = async () => {
    await navigator.clipboard.writeText(sql);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}

      <div className="max-w-7xl mx-auto p-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Panel - Input & Upload */}
          <div className="lg:col-span-1 space-y-4">
            {/* Data Upload Card */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="font-semibold mb-3 flex items-center gap-2">
                <Database className="w-5 h-5 text-blue-600" />
                Upload Your Data
              </h3>

              {!uploadedDataset ? (
                <div>
                  <label className="block">
                    <input
                      type="file"
                      accept=".csv,.xlsx,.xls"
                      onChange={handleFileUpload}
                      disabled={uploading}
                      className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 disabled:opacity-50"
                    />
                  </label>
                  <p className="text-xs text-gray-500 mt-2">
                    Upload CSV or Excel file (max 10MB)
                  </p>
                  {uploading && (
                    <div className="mt-3 text-sm text-blue-600">
                      Uploading and processing...
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="p-3 bg-green-50 rounded border border-green-200">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 text-green-800 font-medium">
                          <CheckCircle className="w-4 h-4" />
                          Dataset Loaded
                        </div>
                        <div className="text-sm text-green-700 mt-1">
                          <div>{uploadedDataset.row_count} rows</div>
                          <div>{Object.keys(uploadedDataset.schema).length} columns</div>
                          <div className="text-xs mt-1">
                            Table: <code>{uploadedDataset.table_name}</code>
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={onDeleteDataset}
                        className="text-red-600 hover:text-red-800"
                        title="Remove dataset"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  <button
                    onClick={() => setActiveTab("schema")}
                    className="w-full text-sm text-blue-600 hover:text-blue-800 underline"
                  >
                    View Schema Details
                  </button>
                </div>
              )}
            </div>

            {/* Query Input Card */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-xl font-bold mb-4">Ask Your Question</h2>

              <textarea
                value={nl}
                onChange={(e) => setNl(e.target.value)}
                rows={8}
                className="w-full p-3 border border-gray-300 rounded mb-4 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder={
                  uploadedDataset
                    ? "e.g., Show customers from UK with revenue > 1000"
                    : "Upload data first, or enter any question to see an example"
                }
              />

              <button
                onClick={onConvert}
                disabled={loading || !nl.trim()}
                className="w-full bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2 font-medium"
              >
                {loading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Converting...
                  </>
                ) : (
                  "Convert to SQL"
                )}
              </button>

              {!uploadedDataset && (
                <div className="mt-3 p-3 bg-yellow-50 rounded border border-yellow-200">
                  <div className="flex gap-2">
                    <AlertCircle className="w-4 h-4 text-yellow-600 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-yellow-800">
                      Without uploaded data, the AI will generate example schemas and sample results.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Right Panel - Output */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow">
              {/* Tabs */}
              <div className="border-b">
                <div className="flex">
                  <button
                    onClick={() => setActiveTab("query")}
                    className={`px-6 py-3 font-medium transition-colors ${activeTab === "query"
                      ? "border-b-2 border-blue-600 text-blue-600"
                      : "text-gray-600 hover:text-gray-900"
                      }`}
                  >
                    Query & Results
                  </button>
                  <button
                    onClick={() => setActiveTab("data")}
                    className={`px-6 py-3 font-medium transition-colors ${activeTab === "data"
                      ? "border-b-2 border-blue-600 text-blue-600"
                      : "text-gray-600 hover:text-gray-900"
                      }`}
                  >
                    Data Preview
                  </button>
                  <button
                    onClick={() => setActiveTab("schema")}
                    className={`px-6 py-3 font-medium transition-colors ${activeTab === "schema"
                      ? "border-b-2 border-blue-600 text-blue-600"
                      : "text-gray-600 hover:text-gray-900"
                      }`}
                  >
                    Schema
                  </button>
                </div>
              </div>

              <div className="p-6">
                {error && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded flex gap-2">
                    <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
                    <div className="text-sm text-red-700">{error}</div>
                  </div>
                )}

                {/* Query Tab */}
                {activeTab === "query" && (
                  <div className="space-y-4">
                    {sql && (
                      <div>
                        <div className="flex justify-between items-center mb-2">
                          <div className="flex items-center gap-3">
                            <h3 className="font-semibold">Generated SQL</h3>
                            {confidence && (
                              <span className="text-xs px-2 py-1 rounded bg-blue-100 text-blue-800">
                                Confidence: {(confidence * 100).toFixed(0)}%
                              </span>
                            )}
                          </div>
                          <button
                            onClick={onRunSQL}
                            disabled={!uploadedDataset || loading}
                            className="flex items-center gap-2 bg-green-600 text-white px-3 py-1.5 rounded text-sm hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <Play className="w-4 h-4" />
                            {loading ? "Running..." : "Run Query"}
                          </button>
                        </div>

                        {reasoning && (
                          <div className="mb-2 text-sm text-gray-600 italic">
                            💡 {reasoning}
                          </div>
                        )}

                        <pre className="relative bg-gray-900 text-green-400 p-4 rounded text-sm overflow-auto font-mono">
                          {sql}

                          {/* Copy Button inside bottom-right */}
                          <button
                            onClick={handleCopy}
                            className="absolute bottom-2 right-2 p-1.5 bg-gray-800 hover:bg-gray-700 rounded-md transition"
                          >
                            {copied ? (
                              <Check className="w-4 h-4 text-green-400" />
                            ) : (
                              <Copy className="w-4 h-4 text-gray-300" />
                            )}
                          </button>
                        </pre>
                      </div>
                    )}

                    {result && result.length > 0 && (
                      <div>
                        <div className="flex justify-between items-center mb-3">
                          <h3 className="font-semibold">
                            Results ({result.length} rows)
                          </h3>
                          <button
                            onClick={exportToCSV}
                            className="flex items-center gap-2 text-blue-600 hover:text-blue-800 text-sm"
                          >
                            <Download className="w-4 h-4" />
                            Export CSV
                          </button>
                        </div>
                        <div className="overflow-auto border rounded max-h-96">
                          <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50 sticky top-0">
                              <tr>
                                {Object.keys(result[0]).map((key) => (
                                  <th
                                    key={key}
                                    className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                                  >
                                    {key}
                                  </th>
                                ))}
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                              {result.map((row, idx) => (
                                <tr key={idx} className="hover:bg-gray-50">
                                  {Object.values(row).map((val, i) => (
                                    <td
                                      key={i}
                                      className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap"
                                    >
                                      {val !== null && val !== undefined ? String(val) : "NULL"}
                                    </td>
                                  ))}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {!sql && !result && (
                      <div className="text-center py-16 text-gray-400">
                        <Table2 className="w-16 h-16 mx-auto mb-4 opacity-30" />
                        <p>Enter a query and click "Convert to SQL" to begin</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Data Preview Tab */}
                {activeTab === "data" && (
                  <div>
                    {datasetPreview ? (
                      <>
                        <h3 className="font-semibold mb-3">
                          Data Preview (first 5 rows)
                        </h3>
                        <div className="overflow-auto border rounded max-h-96">
                          <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50 sticky top-0">
                              <tr>
                                {Object.keys(datasetPreview[0]).map((key) => (
                                  <th
                                    key={key}
                                    className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase"
                                  >
                                    {key}
                                  </th>
                                ))}
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                              {datasetPreview.map((row, idx) => (
                                <tr key={idx} className="hover:bg-gray-50">
                                  {Object.values(row).map((val, i) => (
                                    <td
                                      key={i}
                                      className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap"
                                    >
                                      {val !== null && val !== undefined ? String(val) : "NULL"}
                                    </td>
                                  ))}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </>
                    ) : (
                      <div className="text-center py-16 text-gray-400">
                        <Upload className="w-16 h-16 mx-auto mb-4 opacity-30" />
                        <p>Upload a CSV or Excel file to preview your data</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Schema Tab */}
                {activeTab === "schema" && (
                  <div>
                    {uploadedDataset ? (
                      <>
                        <h3 className="font-semibold mb-4">Schema Information</h3>
                        <div className="bg-gray-50 rounded p-4 space-y-4">
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <span className="text-sm font-medium text-gray-600">Table Name:</span>
                              <code className="block mt-1 bg-white px-3 py-2 rounded text-sm">
                                {uploadedDataset.table_name}
                              </code>
                            </div>
                            <div>
                              <span className="text-sm font-medium text-gray-600">Row Count:</span>
                              <div className="mt-1 bg-white px-3 py-2 rounded text-sm font-medium">
                                {uploadedDataset.row_count.toLocaleString()}
                              </div>
                            </div>
                          </div>

                          <div>
                            <span className="text-sm font-medium text-gray-600 block mb-2">
                              Columns ({Object.keys(uploadedDataset.schema).length}):
                            </span>
                            <div className="space-y-2">
                              {Object.entries(uploadedDataset.schema).map(([name, type]) => (
                                <div
                                  key={name}
                                  className="flex items-center gap-3 bg-white px-3 py-2 rounded"
                                >
                                  <code className="text-blue-600 font-mono text-sm flex-1">
                                    {name}
                                  </code>
                                  <span className="text-gray-400">→</span>
                                  <span className="text-gray-600 text-sm font-medium">
                                    {type}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="text-center py-16 text-gray-400">
                        <Database className="w-16 h-16 mx-auto mb-4 opacity-30" />
                        <p>Upload data to view schema information</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}